/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import shaka from "shaka-player";

import { ClientSideAdvertisement, PlayerReferenceBase } from "../../types";
import { AdTracker } from "../CurrentAdTracker";
import { EventManagerShaka } from "../EventManager";
import { PlayerTrackerShaka } from "../PlayerTracker";

type GoogleIMA = typeof google.ima;

interface PlayerReferenceShaka extends PlayerReferenceBase {
  playerEventsManager: shaka.util.EventManager;
}

export class ClientSideAdvertisementShaka implements ClientSideAdvertisement {
  private ima: GoogleIMA;
  private readonly player: shaka.Player;
  private readonly refs: PlayerReferenceShaka;

  private playerTracker: PlayerTrackerShaka;
  private adEventsManager: EventManagerShaka | undefined;

  private displayContainer: google.ima.AdDisplayContainer | undefined;
  private loader: google.ima.AdsLoader | undefined;

  private startMuted = false;
  private playerInErrorState = false;

  constructor(
    ima: GoogleIMA,
    player: shaka.Player,
    refs: PlayerReferenceShaka,
  ) {
    this.ima = ima;
    this.player = player;
    this.refs = refs;
    this.playerTracker = new PlayerTrackerShaka(
      this.player,
      this.onPlayerResize,
      this.refs.playerEventsManager,
    );

    this.refs.playerEventsManager.listen(
      this.player.getMediaElement(),
      "adlinearmodeend",
      () => {
        window.setTimeout(() => {
          !this.player.getMediaElement()?.ended &&
            this.player.getMediaElement()?.play();
        }, 100);
      },
    );
  }

  initialize(tagUrl: string, startMuted: boolean) {
    this.startMuted = startMuted;

    const { videoElement, imaContainerElement, playerEventsManager } =
      this.refs;

    this.dispose();
    this.playerTracker.initialize();

    this.displayContainer = new google.ima.AdDisplayContainer(
      imaContainerElement,
      videoElement,
    );

    const loader = (this.loader = new google.ima.AdsLoader(
      this.displayContainer,
    ));

    loader.addEventListener(
      google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      this.onManagerLoaded,
      false,
    );

    loader.addEventListener(
      google.ima.AdErrorEvent.Type.AD_ERROR,
      this.onLoadedError,
      false,
    );

    playerEventsManager.listenOnce(
      this.player.getMediaElement(),
      shaka.ads.Utils.ADS_LOADED,
      () => {
        this.adEventsManager?.initialize(this.playerTracker.dimension, () => {
          this.displayContainer?.initialize();
        });
      },
    );

    playerEventsManager.listen(this.player, "complete", () => {
      loader.contentComplete();

      this.adEventsManager
        ? this.adEventsManager.contentComplete()
        : this.player
            .getMediaElement()
            ?.dispatchEvent(new CustomEvent("nopostroll"));
    });

    loader.requestAds(this.prepareAdsRequest(tagUrl));
  }

  dispose() {
    this.playerTracker.dispose();
    this.adEventsManager?.dispose();
  }

  onPlayerError() {
    this.playerInErrorState = true;
    this.dispose();
  }

  onPlayerReload() {
    this.playerInErrorState = false;
  }

  get tracker(): AdTracker {
    if (!this.adEventsManager) {
      throw new Error("Advertisement event manager is not initialized");
    }

    return this.adEventsManager.adTracker;
  }

  get cuePoints(): number[] {
    return this.adEventsManager?.cuePoints || [];
  }

  volume(value: number) {
    this.adEventsManager?.volume(value);
  }

  muted(state: boolean) {
    this.adEventsManager?.muted(state);
  }

  pause() {
    this.adEventsManager?.pause();
  }

  resume() {
    this.adEventsManager?.resume();
  }

  skip() {
    this.adEventsManager?.skip();
  }

  private prepareAdsRequest(tagUrl: string) {
    const { width, height } = this.playerTracker.dimension;

    const adsRequest = new google.ima.AdsRequest();

    adsRequest.adTagUrl = tagUrl;
    adsRequest.linearAdSlotWidth = width;
    adsRequest.linearAdSlotHeight = height;

    adsRequest.setAdWillAutoPlay(true);

    return adsRequest;
  }

  private onManagerLoaded = (event: google.ima.AdsManagerLoadedEvent) => {
    const manager = event.getAdsManager(
      this.playerTracker.playhead,
      this.renderingSettings(),
    );

    this.adEventsManager?.dispose();
    this.adEventsManager = new EventManagerShaka(
      manager,
      this.player,
      this.startMuted,
    );

    if (this.playerInErrorState) {
      manager.discardAdBreak();
      manager.destroy();
    } else {
      this.refs.playerEventsManager.listenOnce(
        this.player.getMediaElement(),
        "timeupdate",
        () => {
          this.player
            .getMediaElement()
            ?.dispatchEvent(new CustomEvent(shaka.ads.Utils.ADS_LOADED));
        },
      );
    }
  };

  private onLoadedError = (event: google.ima.AdErrorEvent) => {
    console.error("Unable to load advertisement system:", event.getError());

    this.player.getMediaElement()?.dispatchEvent(new CustomEvent("nopreroll"));
    this.player.getMediaElement()?.dispatchEvent(new CustomEvent("nopostroll"));

    this.adEventsManager?.dispose();
  };

  private onPlayerResize = (width: number, height: number) => {
    this.adEventsManager?.onPlayerResize(width, height);
  };

  onEnterFullScreen(): void {
    this.adEventsManager?.onEnterFullScreen();
  }

  onLeaveFullScreen(): void {
    const width = this.playerTracker.getPlayerWidth();
    const height = this.playerTracker.getPlayerHeight();

    this.adEventsManager?.onLeaveFullScreen(width, height);
  }

  private renderingSettings() {
    const settings = new google.ima.AdsRenderingSettings();
    settings.restoreCustomPlaybackStateOnAdBreakComplete = true;

    return settings;
  }
}
